import ContentPage from "./ContentPage";
import PlanCards from "../ui/PlanCards";
import Accordion from "../ui/Accordion";
import { Check } from "../ui/icons";
import {
  GST_RATE_PCT,
  INCLUDED,
  TIERS,
  TRIAL_DAYS,
  capLabel,
  formatPrice,
  perDay,
  perMember,
} from "../data/pricing";
import { COMPANY } from "../data/company";
import "./pricing.css";

/*
 * What happens between signing up and paying, in the order it happens. A real
 * sequence, so the numbers carry information.
 */
const STEPS = [
  {
    title: `${TRIAL_DAYS} days free`,
    body: "Add your real members, take payments, send reminders. No card, nothing to install.",
  },
  {
    title: "Pick a plan when it ends",
    body: "Pay once for the term you choose, through Razorpay. Nothing renews on its own.",
  },
  {
    title: "Or don't, and lose nothing",
    body: "The app turns read-only. Every member and payment stays exactly where it was until you pay.",
  },
];

/* The example gym for the per-member line. Labelled as an example on the page. */
const EXAMPLE_MEMBERS = 300;
const STARTER = TIERS.find((t) => t.tier === "starter") ?? TIERS[0];
const YEAR = 12;

/* What the price buys, in plain words. Each line is true of the product today. */
const WHAT_YOU_PAY_FOR = [
  "No ads. You pay for the software, so nobody else has to.",
  "Every feature on every plan. Tiers differ by member and staff limits and monthly Auto reminder credits.",
  `A ${TRIAL_DAYS}-day trial with no card, so you test it on your real members first.`,
  `Plus ${GST_RATE_PCT}% GST at checkout, with a GST invoice for every payment.`,
];

/*
 * The questions a gym owner asks before paying, answered from how billing
 * actually behaves (backend/services/memberCap.js, billingTerms.js,
 * entitlement.js), not from how it might.
 */
const QUESTIONS = [
  {
    q: "Is GST included in these prices?",
    a: `No. Prices are before GST; GST at ${GST_RATE_PCT}% is added at checkout and every payment comes with a GST invoice. Razorpay may add a small convenience fee for some payment methods, shown before you pay.`,
  },
  {
    q: "Does my plan renew automatically?",
    a: "No. When a term ends you decide whether to buy another. Paying early adds the new term on top of the days you already have, so you never lose time by renewing early.",
  },
  {
    q: "What happens if I go over my member limit?",
    a: "Nothing happens to the members you already have. You just cannot add new ones until you move up a plan.",
  },
  {
    q: "Can I upgrade in the middle of a term?",
    a: "Yes. You pay only the difference for the days left on your current plan, and your renewal date stays the same.",
  },
  {
    q: "What if my trial ends and I have not paid?",
    a: "The app becomes read-only. Nothing is deleted. Pay whenever you are ready and you pick up where you left off.",
  },
  {
    q: "Do my members or trainers pay anything?",
    a: "No. Members never pay us anything, and trainers sign in free, up to your plan's staff limit.",
  },
];

/**
 * The public price list.
 *
 * Four answers, in the order a buyer needs them: what it costs (the cards),
 * how paying actually works (the steps), what the tiers really differ by (the
 * table, which mostly shows that they do not), and the objections (the
 * questions). Every number on the page is read from the catalog.
 */
export default function Pricing() {
  return (
    <ContentPage
      title="Pricing"
      lead={`Gym management software price in India: fixed terms, paid upfront, plus GST. Every club starts with ${TRIAL_DAYS} days free, with no card.`}
    >
      <div className="pricing__wide">
        <PlanCards headingLevel={2} />
      </div>

      {STARTER?.prices[YEAR] && (
        <p className="t-body content-page__paragraph pricing__unit">
          {STARTER.name} on a {YEAR}-month term works out to{" "}
          <strong>{formatPrice(perDay(STARTER.prices[YEAR], YEAR))} a day</strong>. As an example,
          for a gym with {EXAMPLE_MEMBERS} members that is about{" "}
          <strong>
            {formatPrice(perMember(STARTER.prices[YEAR], EXAMPLE_MEMBERS, YEAR))} per member a year
          </strong>
          . Both are before GST.
        </p>
      )}

      <section className="pricing__block" aria-labelledby="pricing-for">
        <h2 className="content-page__heading" id="pricing-for">
          What you pay for
        </h2>
        <ul className="content-page__list">
          {WHAT_YOU_PAY_FOR.map((line) => (
            <li className="content-page__item" key={line}>
              {line}
            </li>
          ))}
        </ul>
      </section>

      <section className="pricing__block" aria-labelledby="pricing-trial">
        <h2 className="content-page__heading" id="pricing-trial">
          How paying works
        </h2>
        <ol className="pricing__steps">
          {STEPS.map((step, i) => (
            <li className="pricing__step" key={step.title}>
              <span className="pricing__step-n">{i + 1}</span>
              <h3 className="pricing__step-title">{step.title}</h3>
              <p className="pricing__step-body">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="pricing__block" aria-labelledby="pricing-compare">
        <h2 className="content-page__heading" id="pricing-compare">
          What every plan includes
        </h2>
        <p className="t-body content-page__paragraph">
          Tiers differ by how many members and staff you can have, never by
          what the app can do.
        </p>
        <div className="pricing__table-wrap">
          <table className="pricing__table">
            <thead>
              <tr>
                <th scope="col">
                  <span className="visually-hidden">Feature</span>
                </th>
                {TIERS.map((tier) => (
                  <th scope="col" key={tier.tier}>
                    {tier.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Members</th>
                {TIERS.map((tier) => (
                  <td key={tier.tier}>{capLabel(tier.maxMembers)}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Trainers and staff</th>
                {TIERS.map((tier) => (
                  <td key={tier.tier}>{capLabel(tier.maxStaff)}</td>
                ))}
              </tr>
              {INCLUDED.map((feature) => (
                <tr key={feature}>
                  <th scope="row">{feature}</th>
                  {TIERS.map((tier) => (
                    <td key={tier.tier}>
                      <Check className="pricing__yes" aria-hidden="true" />
                      <span className="visually-hidden">Included</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pricing__block" aria-labelledby="pricing-faq">
        <h2 className="content-page__heading" id="pricing-faq">
          Questions about paying
        </h2>
        <Accordion
          items={QUESTIONS}
          getKey={(item) => item.q}
          render={(item) => <p className="t-body">{item.a}</p>}
        />
        <p className="t-body content-page__paragraph pricing__ask">
          Something else? Write to{" "}
          <a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a>.
          Payments are taken through Razorpay; card and bank details never
          reach our servers.
        </p>
      </section>
    </ContentPage>
  );
}
